import { useEffect, useRef, useCallback } from "react";
import apiClient from "../Utils/apiClient";

const REPEAT_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * VoiceDeterrencePlayer
 *
 * Fetches active voice deterrence messages from the API and reads them aloud
 * using the browser's SpeechSynthesis API.
 *
 * Props:
 *   - escortType: string (e.g. "driver-escort", "short-distance")
 *   - audience:   string (e.g. "driver", "passenger")  — optional
 *
 * Behaviour:
 *   1. Plays all messages immediately on mount.
 *   2. Repeats every 10 minutes.
 *   3. Continues in background tabs (SpeechSynthesis is not paused on blur).
 *   4. Stops and cleans up on unmount.
 */
const VoiceDeterrencePlayer = ({ escortType = "driver-escort", audience }) => {
  const messagesRef = useRef([]);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Fetch messages from the API
  const fetchMessages = useCallback(async () => {
    try {
      const params = {};
      if (escortType) params.escortType = escortType;
      if (audience) params.audience = audience;

      const response = await apiClient.get("/voice-deterrence/active", { params });
      const data = Array.isArray(response?.data) ? response.data : [];
      messagesRef.current = data;
      return data;
    } catch (err) {
      console.warn("[VoiceDeterrencePlayer] Failed to fetch messages:", err);
      return [];
    }
  }, [escortType, audience]);

  // Speak a single message using SpeechSynthesis
  const speakMessage = useCallback((text) => {
    return new Promise((resolve) => {
      if (!window.speechSynthesis || !text) {
        resolve();
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "en-US";
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;
      utterance.onend = resolve;
      utterance.onerror = () => resolve();

      window.speechSynthesis.speak(utterance);
    });
  }, []);

  // Play all messages in sequence
  const playAllMessages = useCallback(async () => {
    if (!isMountedRef.current) return;

    const messages = messagesRef.current;
    if (!messages || messages.length === 0) return;

    // Cancel any ongoing speech before starting fresh
    window.speechSynthesis?.cancel();

    for (const msg of messages) {
      if (!isMountedRef.current) break;
      const text = msg?.message || msg?.text || "";
      if (text.trim()) {
        await speakMessage(text);
      }
    }
  }, [speakMessage]);

  // Fetch + play, then set up the 10-minute repeat
  useEffect(() => {
    isMountedRef.current = true;

    const init = async () => {
      await fetchMessages();
      await playAllMessages();

      // Repeat every 10 minutes
      intervalRef.current = setInterval(async () => {
        if (!isMountedRef.current) return;
        await fetchMessages(); // re-fetch in case admin updated messages
        await playAllMessages();
      }, REPEAT_INTERVAL_MS);
    };

    init();

    return () => {
      isMountedRef.current = false;
      window.speechSynthesis?.cancel();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchMessages, playAllMessages]);

  // This component renders nothing — it's purely a side-effect player
  return null;
};

export default VoiceDeterrencePlayer;
